import { c as _c } from "react/compiler-runtime";
import { Type } from '@repo/design-system/components/ui/type';
import { generateErrorId } from '@repo/design-system/utils/accessibility';
import { css, cva, cx } from '@repo/panda/css';
import MDEditor, { commands } from '@uiw/react-md-editor';
export interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  errorText?: string;
  helperText?: string;
  fullWidth?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'dark' | 'light';
  label?: string;
  required?: boolean;
  className?: string;
  'aria-describedby'?: string;
  id?: string;
  preview?: 'edit' | 'preview' | 'live';
  height?: number;
  maxLength?: number;
}
const mdEditorStyles = cva({
  base: {
    width: 'full',
    borderRadius: '12',
    border: '[1px solid transparent]',
    transition: '[all 0.2s ease]',
    outline: 'none',
    _disabled: {
      opacity: 0.5,
      cursor: 'not-allowed'
    }
    // Remove default background (handled by variant)
  },
  variants: {
    variant: {
      dark: {
        backgroundColor: 'whiteAlpha.5',
        color: 'whiteAlpha.80',
        _focusVisible: {
          backgroundColor: 'whiteAlpha.10'
        },
        _hover: {
          backgroundColor: 'whiteAlpha.10'
        },
        _placeholder: {
          color: 'whiteAlpha.30'
        }
      },
      light: {
        backgroundColor: 'wemod.black.10',
        color: 'wemod.black.80',
        _focusVisible: {
          backgroundColor: 'wemod.black.20'
        },
        _hover: {
          backgroundColor: 'wemod.black.20'
        },
        _placeholder: {
          color: 'wemod.black.50'
        }
      }
    },
    size: {
      sm: {
        fontSize: 'md',
        minHeight: '[80px]'
      },
      md: {
        fontSize: 'lg',
        minHeight: '[100px]'
      },
      lg: {
        fontSize: 'xl',
        minHeight: '[120px]'
      },
      xl: {
        fontSize: '2xl',
        minHeight: '[140px]'
      }
    }
  },
  defaultVariants: {
    variant: 'dark',
    size: 'md'
  }
});
const errorStyles = cva({
  base: {
    fontSize: 'sm',
    marginTop: '4'
  },
  variants: {
    variant: {
      dark: {
        color: 'error'
      },
      light: {
        color: 'error.600'
      }
    }
  },
  defaultVariants: {
    variant: 'dark'
  }
});
const helperTextStyles = cva({
  base: {
    fontSize: 'sm',
    marginTop: '4'
  },
  variants: {
    variant: {
      dark: {
        color: 'wemod.black.50'
      },
      light: {
        color: 'wemod.black.60'
      }
    }
  },
  defaultVariants: {
    variant: 'dark'
  }
});
const labelStyles = cva({
  base: {},
  variants: {
    required: {
      true: {
        _after: {
          content: '" *"',
          color: 'error'
        }
      }
    }
  },
  defaultVariants: {
    required: false
  }
});
export const MarkdownEditor = t0 => {
  const $ = _c(47);
  const {
    value,
    onChange,
    errorText,
    helperText,
    fullWidth: t1,
    size: t2,
    variant: t3,
    label,
    required: t4,
    className,
    "aria-describedby": ariaDescribedBy,
    id,
    preview: t5,
    height,
    maxLength
  } = t0;
  const fullWidth = t1 === undefined ? false : t1;
  const size = t2 === undefined ? "md" : t2;
  const variant = t3 === undefined ? "dark" : t3;
  const required = t4 === undefined ? false : t4;
  const preview = t5 === undefined ? "edit" : t5;
  const hasError = Boolean(errorText);
  const hasHelperText = Boolean(helperText);
  const hasLabel = Boolean(label);
  let t6;
  if ($[0] !== ariaDescribedBy || $[1] !== hasError) {
    t6 = generateErrorId("markdown-editor", hasError, ariaDescribedBy);
    $[0] = ariaDescribedBy;
    $[1] = hasError;
    $[2] = t6;
  } else {
    t6 = $[2];
  }
  const errorId = t6;
  let t7;
  if ($[3] !== hasLabel || $[4] !== id) {
    t7 = hasLabel ? `markdown-editor-${Math.random().toString(36).substr(2, 9)}` : id;
    $[3] = hasLabel;
    $[4] = id;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  const editorId = t7;
  const t8 = fullWidth ? "full" : "fit";
  let t9;
  if ($[6] !== t8) {
    t9 = css({
      width: t8
    });
    $[6] = t8;
    $[7] = t9;
  } else {
    t9 = $[7];
  }
  const rootStyles = t9;
  const t10 = variant === "dark" ? "dark" : "light";
  let t11;
  if ($[8] !== editorId || $[9] !== hasLabel || $[10] !== label || $[11] !== required) {
    t11 = hasLabel && <label htmlFor={editorId} className={labelStyles({
      required
    })}><Type variant="inputHint">{label}</Type></label>;
    $[8] = editorId;
    $[9] = hasLabel;
    $[10] = label;
    $[11] = required;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] !== onChange) {
    t12 = v => onChange(v || "");
    $[13] = onChange;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  const t13 = hasError ? errorId : ariaDescribedBy;
  let t14;
  if ($[15] !== className || $[16] !== size || $[17] !== variant) {
    t14 = cx(mdEditorStyles({
      variant,
      size
    }), className);
    $[15] = className;
    $[16] = size;
    $[17] = variant;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  let t15;
  if ($[19] !== maxLength) {
    t15 = {
      maxLength
    };
    $[19] = maxLength;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  let t16;
  if ($[21] !== editorId || $[22] !== hasError || $[23] !== height || $[24] !== preview || $[25] !== t12 || $[26] !== t13 || $[27] !== t14 || $[28] !== t15 || $[29] !== value) {
    t16 = <MDEditor value={value} onChange={t12} preview={preview} height={height} id={editorId} aria-describedby={t13} aria-invalid={hasError} className={t14} textareaProps={t15} commandsFilter={_temp} />;
    $[21] = editorId;
    $[22] = hasError;
    $[23] = height;
    $[24] = preview;
    $[25] = t12;
    $[26] = t13;
    $[27] = t14;
    $[28] = t15;
    $[29] = value;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== hasHelperText || $[32] !== helperText || $[33] !== variant) {
    t17 = hasHelperText && <div className={helperTextStyles({
      variant
    })}>{helperText}</div>;
    $[31] = hasHelperText;
    $[32] = helperText;
    $[33] = variant;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== errorId || $[36] !== errorText || $[37] !== hasError || $[38] !== variant) {
    t18 = hasError && <div className={errorStyles({
      variant
    })} id={errorId} role="alert" aria-live="polite">{errorText}</div>;
    $[35] = errorId;
    $[36] = errorText;
    $[37] = hasError;
    $[38] = variant;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== rootStyles || $[41] !== t10 || $[42] !== t11 || $[43] !== t16 || $[44] !== t17 || $[45] !== t18) {
    t19 = <div className={rootStyles} data-color-mode={t10}>{t11}{t16}{t17}{t18}</div>;
    $[40] = rootStyles;
    $[41] = t10;
    $[42] = t11;
    $[43] = t16;
    $[44] = t17;
    $[45] = t18;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  return t19;
};
function _temp(cmd) {
  const excludeCommands = [commands.codeBlock, commands.image, commands.comment, commands.fullscreen, commands.code];
  if (excludeCommands.includes(cmd)) {
    return false;
  }
  return cmd;
}